import React, { useState, useEffect } from 'react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
  CommandShortcut
} from '@/components/ui/command';
import { useTheme } from '../contexts/ThemeContext';
import type { ThemeMode, ColorScheme } from '../contexts/ThemeContext';
import type { Tenancy } from '@/contexts/TenancyContext';
import { Check, Moon, Sun, Monitor } from 'lucide-react';

// Using Tenancy type from context

interface TenancyQuickSwitcherProps {
  tenancies: Tenancy[];
  onSwitch: (tenancy: Tenancy) => void;
  // Optional controlled props; if not provided, component manages its own state
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const TenancyQuickSwitcher: React.FC<TenancyQuickSwitcherProps> = ({ tenancies, onSwitch, open: openProp, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [logoErrors, setLogoErrors] = useState<{[key: string]: boolean}>({});
  const [themeSearch, setThemeSearch] = useState('');
  const { mode, setMode, primaryColor, setPrimaryColor, accentColor, setAccentColor } = useTheme();

  const isOpen = openProp !== undefined ? openProp : internalOpen;
  const setOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };

  // Handle logo load errors
  const handleLogoError = (tenancyId: string) => {
    setLogoErrors(prev => ({
      ...prev,
      [tenancyId]: true
    }));
  };

  // Cmd/Ctrl+K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Toggle open state
        setOpen(!isOpen);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  const colorOptions = [
    { name: 'Default', value: 'default' },
    { name: 'Blue', value: 'blue' },
    { name: 'Purple', value: 'purple' },
    { name: 'Green', value: 'green' },
    { name: 'Orange', value: 'orange' },
    { name: 'Pink', value: 'pink' },
    { name: 'Red', value: 'red' },
  ];

  // Helper function to check if search term matches theme options
  const matchesThemeSearch = (searchTerm: string): boolean => {
    const lowerCaseSearch = searchTerm.toLowerCase();
    // Check for theme-related terms
    const themeTerms = ['theme', 'light', 'dark', 'system'];
    const colorTerms = ['color', 'colour', 'col', 'cor'];
    const colorNames = colorOptions.map(color => color.name.toLowerCase());

    // Check if search term matches any theme terms
    if (themeTerms.some(term => lowerCaseSearch.includes(term))) {
      return true;
    }

    // Check if search term matches any color terms or color names
    return (
      colorTerms.some(term => lowerCaseSearch.includes(term)) ||
      colorNames.some(colorName => lowerCaseSearch.includes(colorName))
    );
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Switch tenancy or theme settings..."
        value={search}
        onValueChange={(value) => {
          setSearch(value);
          // Set themeSearch if search starts with "theme" or "color"
          if (value.toLowerCase().startsWith('theme') || value.toLowerCase().startsWith('color')) {
            setThemeSearch(value);
          } else {
            setThemeSearch('');
          }
        }}
        autoFocus
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Tenancies */}
        <CommandGroup heading="Tenancies">
          {tenancies
            .filter((tenancy) =>
              tenancy.name.toLowerCase().includes(search.toLowerCase())
            )
            .map((tenancy, idx) => (
              <CommandItem
                key={tenancy.id || tenancy.name}
                onSelect={() => {
                  onSwitch(tenancy);
                  setOpen(false);
                  setSearch('');
                }}
                className="flex items-center gap-2"
                data-tenancy-id={`qs-${tenancy.id || tenancy.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {logoErrors[tenancy.id || tenancy.name] || !tenancy.logo ? (
                  <div 
                    className="size-5 flex items-center justify-center font-bold bg-gray-100 text-foreground! rounded-sm border"
                    style={{ marginRight: 8 }}
                  >
                    {tenancy.name.charAt(0).toUpperCase()}
                  </div>
                ) : (
                  <img
                    src={tenancy.logo}
                    alt={tenancy.name}
                    className="size-5 rounded-sm object-contain bg-white border tenancy-qs-logo"
                    style={{ marginRight: 8 }}
                    onError={() => handleLogoError(tenancy.id || tenancy.name)}
                  />
                )}
                <span className="flex-1">{tenancy.name}</span>
                {idx < 10 && (
                  <CommandShortcut>
                    {(idx === 9 ? '0' : (idx + 1).toString())}
                  </CommandShortcut>
                )}
              </CommandItem>
            ))}
        </CommandGroup>

        {/* Theme Settings */}
        <CommandGroup heading="Theme Mode">
          <div className="flex gap-2 p-2">
            {(['light', 'dark', 'system'] as ThemeMode[]).map((themeMode) => (
              <CommandItem
                key={themeMode}
                onSelect={() => {
                  console.log('TenancyQuickSwitcher: changing mode from', mode, 'to', themeMode, {
                    currentMode: mode,
                    newMode: themeMode,
                    stackTrace: new Error().stack?.split('\n').slice(1, 5).join('\n')
                  });
                  setMode(themeMode);
                  setOpen(false);
                }}
                className={`flex justify-center items-center gap-2 w-full ${
                  mode === themeMode ? 'bg-accent/50' : ''
                }`}
              >
                {themeMode === 'light' && <Sun size={18} />}
                {themeMode === 'dark' && <Moon size={18} />}
                {themeMode === 'system' && <Monitor size={18} />}
                <span>{themeMode.charAt(0).toUpperCase() + themeMode.slice(1)}</span>
                {mode === themeMode && <Check size={16} className="ml-1" />}
              </CommandItem>
            ))}
          </div>
        </CommandGroup>

        <CommandGroup heading="Primary Color">
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 p-2">
            {colorOptions
              .filter(color => color.name.toLowerCase().includes(search.toLowerCase()))
              .map((color) => (
                <CommandItem
                  key={`primary-${color.value}`}
                  onSelect={() => {
                    setPrimaryColor(color.value as ColorScheme);
                    setOpen(false);
                  }}
                  className={`relative w-12 h-12 p-2 ${
                    primaryColor === color.value ? 'bg-accent/50' : ''
                  }`}
                  data-search={color.name.toLowerCase()}
                >
                  <div
                    className={`w-8 h-8 rounded-full ${
                      color.value === 'default'
                        ? 'bg-amber-500'
                        : `bg-${color.value}-600`
                    } flex items-center justify-center`}
                    title={color.name}
                  >
                    {primaryColor === color.value && (
                      <Check className="absolute inset-0 m-auto text-white" size={16} />
                    )}
                  </div>
                </CommandItem>
              ))}
          </div>
        </CommandGroup>

        <CommandGroup heading="Accent Color">
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 p-2">
            {colorOptions
              .filter(color => color.name.toLowerCase().includes(search.toLowerCase()))
              .map((color) => (
                <CommandItem
                  key={`accent-${color.value}`}
                  onSelect={() => {
                    setAccentColor(color.value as ColorScheme);
                    setOpen(false);
                  }}
                  className={`relative w-12 h-12 p-2 ${
                    accentColor === color.value ? 'bg-accent/50' : ''
                  }`}
                  data-search={color.name.toLowerCase()}
                >
                  <div
                    className={`w-8 h-8 rounded-full ${
                      color.value === 'default'
                        ? 'bg-amber-500'
                        : `bg-${color.value}-400`
                    } flex items-center justify-center`}
                    title={color.name}
                  >
                    {accentColor === color.value && (
                      <Check className="absolute inset-0 m-auto text-white" size={16} />
                    )}
                  </div>
                </CommandItem>
              ))}
          </div>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
